import { Directive, ElementRef, OnInit, HostListener, AfterViewInit } from '@angular/core';
import { Table } from 'primeng/table';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

// NOTE Chrome Issue: keyboard repeat events fire as expected, but any changes made to the DOM do not reflect until
// the key is released. This however works fine on FireFox.

// adapted from https://github.com/primefaces/primeng/issues/5429
@Directive({
    selector: '[kui-table-key-handler]',
})
export class KuiTableKeyHandlerDirective implements OnInit, AfterViewInit {

    rowsSelectedDebounce = new Subject<{ originalEvent: KeyboardEvent, data: any, type: string }>();

    scrollBody: HTMLDivElement;

    constructor(
        private table: Table,
        private el: ElementRef
    ) {
        this.rowsSelectedDebounce
            .pipe(debounceTime(500))
            .subscribe(event => {
                this.table.onRowSelect.emit(event);
            });
    }

    ngOnInit() {
        this.el.nativeElement.tabIndex = 1; // without this the keyboard events never reach us
    }

    ngAfterViewInit() {
        this.scrollBody = this.el.nativeElement.querySelector('.ui-table-scrollable-body') as HTMLDivElement;
    }

    @HostListener('keydown.ArrowUp', ['$event'])
    @HostListener('keydown.ArrowDown', ['$event'])
    keyUpDown(event: KeyboardEvent) {
        this.selectRow(event, this.navigateItem((event.key === 'ArrowUp' ? -1 : 1)));
        this.scrollIntoView();
        event.preventDefault();
    }

    @HostListener('keyup.Home', ['$event'])
    keyHome(event: KeyboardEvent) {
        this.selectRow(event, this.table.value[0]);
        setTimeout(() => this.scrollIntoView(), 100);
        event.preventDefault();
    }

    @HostListener('keyup.End', ['$event'])
    keyEnd(event: KeyboardEvent) {
        this.selectRow(event, this.table.value[this.table.value.length - 1]);
        setTimeout(() => this.scrollIntoView(), 100);
        event.preventDefault();
    }

    @HostListener('keyup.PageDown', ['$event'])
    @HostListener('keyup.PageUp', ['$event'])
    keyPage(event: KeyboardEvent) {
        const selected = this.el.nativeElement.querySelector('tr.ui-state-highlight') as HTMLTableRowElement;
        if (selected) {
            this.selectRow(event, this.navigateItem((event.key === 'PageUp' ? -1 : 1) * Math.round(this.scrollBody.clientHeight / selected.clientHeight)));
            setTimeout(() => this.scrollIntoView(), 100);
            event.preventDefault();
        }
    }

    selectRow(event: KeyboardEvent, item: any) {
        if (item) {
            this.table.selection = [item];
            this.rowsSelectedDebounce.next({ originalEvent: event, data: item, type: 'row' });
        }
    }

    scrollIntoView() {
        const selected = this.el.nativeElement.querySelector('tr.ui-state-highlight') as HTMLTableRowElement;
        if (selected) {
            const offsetTop = (selected.clientHeight * 1.5);
            if (selected.offsetTop - this.scrollBody.scrollTop - offsetTop <= 0) {
                this.scrollBody.scrollTop = selected.offsetTop - offsetTop;
            }
            const offsetBottom = selected.offsetTop + (selected.clientHeight * 2.5);
            if ((this.scrollBody.clientHeight + this.scrollBody.scrollTop) - offsetBottom <= 0) {
                this.scrollBody.scrollTop = (offsetBottom - this.scrollBody.clientHeight);
            }
        }
    }

    @HostListener('keydown.ArrowRight', ['$event'])
    @HostListener('keydown.ArrowLeft', ['$event'])
    keyLeftRight(event: KeyboardEvent) {
        this.scrollBody.scrollLeft = this.scrollBody.scrollLeft + (event.key === 'ArrowLeft' ? -100 : 100);
        event.preventDefault();
    }

    navigateItem(num: number): any {
        if (!this.table.selection || !this.table.selection.length) { return []; }
        const idx = this.table.value.indexOf(Array.isArray(this.table.selection) ? this.table.selection[0] : this.table.selection);
        return this.table.value[Math.min(this.table.value.length - 1, Math.max(0, idx + num))];
    }

}
